import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { quotationRequestApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Camera, Plus, Save, Trash2, FileSpreadsheet, QrCode, X,
  ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import { cn } from '../lib/utils';

const ROLES_CAN_EDIT = ['admin', 'super_admin', 'sales', 'production'];

// ─────────────────────────────────────────────────────────────────
// QR Scanner sub-component
// ─────────────────────────────────────────────────────────────────
const SCANNER_ELEMENT_ID = 'qr-scanner-container';

const QRScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const lastCodeRef = useRef({ code: null, at: 0 });
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState(null);

  // iOS Safari + html5-qrcode periodically reject internal promises with
  // "OperationError: The operation was aborted." when the video element is
  // torn down (React StrictMode double-mount, navigation, tab switch, etc.).
  // The rejections bubble up as "Uncaught (in promise)" and show React's
  // dev error overlay. They are benign — the scanner recovers automatically.
  // We silence ONLY this specific message while the scanner is mounted.
  useEffect(() => {
    const handler = (event) => {
      const msg = String(event?.reason?.message || event?.reason || '');
      if (/operation was aborted|abort/i.test(msg) || /OperationError/i.test(msg)) {
        event.preventDefault();
        // Also stop the dev error overlay from picking it up
        event.stopImmediatePropagation?.();
      }
    };
    const errorHandler = (event) => {
      const msg = String(event?.message || event?.error?.message || '');
      if (/operation was aborted/i.test(msg) || /OperationError/i.test(msg)) {
        event.preventDefault();
        event.stopImmediatePropagation?.();
      }
    };
    window.addEventListener('unhandledrejection', handler, true);
    window.addEventListener('error', errorHandler, true);
    return () => {
      window.removeEventListener('unhandledrejection', handler, true);
      window.removeEventListener('error', errorHandler, true);
    };
  }, []);

  useEffect(() => {
    let html5Qrcode;
    let cancelled = false;
    let started = false;

    const onDecode = (decodedText) => {
      // Debounce duplicate reads of the same code within 2 seconds
      const now = Date.now();
      if (decodedText === lastCodeRef.current.code && now - lastCodeRef.current.at < 2000) {
        return;
      }
      lastCodeRef.current = { code: decodedText, at: now };
      try { if (navigator.vibrate) navigator.vibrate(80); } catch { /* noop */ }
      _beep();
      onScan(decodedText);
    };

    const start = async () => {
      // Small delay so React StrictMode's first cleanup runs before we touch the camera.
      // Without this, on iOS the camera can be acquired twice and getUserMedia aborts.
      await new Promise(r => setTimeout(r, 50));
      if (cancelled) return;
      try {
        if (!document.getElementById(SCANNER_ELEMENT_ID)) {
          setError('Scanner container not found');
          return;
        }
        html5Qrcode = new Html5Qrcode(SCANNER_ELEMENT_ID, { verbose: false });
        scannerRef.current = html5Qrcode;

        const config = {
          fps: 10,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
          disableFlip: false,
        };

        // Resolve the rear camera by ID. This is more reliable on iOS Safari than
        // passing { facingMode: 'environment' } directly.
        let cameraTarget = { facingMode: { exact: 'environment' } };
        try {
          const cams = await Html5Qrcode.getCameras();
          if (cams && cams.length > 0) {
            // Prefer cameras labelled with 'back' / 'rear' / 'environment'.
            const rear = cams.find(c => /back|rear|environment/i.test(c.label || '')) || cams[cams.length - 1];
            cameraTarget = rear.id;
          }
        } catch {
          // Fall back to facingMode if enumeration fails
          cameraTarget = { facingMode: 'environment' };
        }

        if (cancelled) return;

        try {
          await html5Qrcode.start(cameraTarget, config, onDecode, () => { /* ignore scan-not-found */ });
        } catch (firstErr) {
          // If `exact: environment` failed (e.g. desktop), retry with relaxed front-or-any
          if (cancelled) return;
          await html5Qrcode.start({ facingMode: 'user' }, config, onDecode, () => { /* ignore */ });
        }

        started = true;
        if (cancelled) {
          await _safeStopAndClear(html5Qrcode);
          return;
        }
        setStarting(false);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.message || String(e) || 'Could not access camera.';
          // Friendlier message for known abort case
          if (/operation was aborted/i.test(msg)) {
            setError('Camera was interrupted. Please tap Stop camera and Scan QR again.');
          } else if (/Permission denied|NotAllowed/i.test(msg)) {
            setError('Camera permission was denied. Enable it in your browser settings, then reload.');
          } else if (/NotFound|NotReadable|OverConstrained/i.test(msg)) {
            setError('No usable camera found on this device.');
          } else {
            setError(msg);
          }
          setStarting(false);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const inst = scannerRef.current;
      if (inst && started) {
        _safeStopAndClear(inst);
      }
    };
  }, [onScan]);

  return (
    <Card className="overflow-hidden border-2 border-fuchsia-300 shadow-xl shadow-fuchsia-500/10" data-testid="qr-scanner-card">
      <CardContent className="p-0 relative">
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Camera className="h-4 w-4" /> Point camera at QR code
          </div>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-white hover:bg-white/20" onClick={onClose} data-testid="qr-scanner-close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative bg-black" style={{ minHeight: 280 }}>
          <div id={SCANNER_ELEMENT_ID} className="w-full" />
          {starting && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Starting camera…
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-xs px-6 text-center bg-black/80">
              <Camera className="h-8 w-8 mb-2 opacity-60" />
              <p>{error}</p>
              <p className="text-[10px] opacity-70 mt-2">On mobile, ensure you're on HTTPS and camera permission is granted in your browser settings.</p>
            </div>
          )}
        </div>
        <p className="text-[11px] text-center text-muted-foreground py-1.5 px-2">
          Each successful scan auto-adds the design. Keep scanning continuously.
        </p>
      </CardContent>
    </Card>
  );
};

function _beep() {
  try {
    // eslint-disable-next-line no-undef
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 1100;
    o.type = 'sine';
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
    o.start(ctx.currentTime);
    o.stop(ctx.currentTime + 0.16);
  } catch { /* noop */ }
}

// Guard against the "Cannot stop, scanner is not running or paused" race that fires
// during React StrictMode's double-mount when stop() is called while start() is still
// in flight. We only call stop() if the scanner is actually in a stoppable state.
async function _safeStopAndClear(inst) {
  try {
    const state = inst.getState ? inst.getState() : null;
    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
      await inst.stop();
    }
  } catch { /* noop */ }
  try { await inst.clear(); } catch { /* noop */ }
}

// ─────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────
const QuotationRequestDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const canEdit = ROLES_CAN_EDIT.includes(user?.role);

  // Header fields
  const [header, setHeader] = useState({
    customer_name: '',
    diamond_quality: '',
    diamond_pricing: '',
    payment_terms: '',
    gold_karat: '',
    remarks: '',
  });
  const [items, setItems] = useState([]); // {id?, qr_code, comment, scanned_at?}
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState(null);

  // Load existing request
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    quotationRequestApi.get(id)
      .then(r => {
        const d = r.data;
        setHeader({
          customer_name: d.customer_name || '',
          diamond_quality: d.diamond_quality || '',
          diamond_pricing: d.diamond_pricing || '',
          payment_terms: d.payment_terms || '',
          gold_karat: d.gold_karat || '',
          remarks: d.remarks || '',
        });
        setItems(d.items || []);
        setHeaderCollapsed(true); // Header was already entered; collapse to give room for scanning
      })
      .catch(() => { toast.error('Failed to load quotation request'); navigate('/quotation-requests'); })
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  // Handlers
  const onScan = useCallback(async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed) return;
    // For new (unsaved) requests, just push locally
    if (isNew) {
      setItems(prev => {
        if (prev.some(p => p.qr_code === trimmed)) {
          toast.warning(`${trimmed} already scanned`);
          return prev;
        }
        toast.success(`Added ${trimmed}`);
        return [...prev, { id: `local-${Date.now()}`, qr_code: trimmed, comment: '', scanned_at: new Date().toISOString() }];
      });
      return;
    }
    // For existing requests, persist immediately
    try {
      if (items.some(p => p.qr_code === trimmed)) {
        toast.warning(`${trimmed} already scanned`);
        return;
      }
      const r = await quotationRequestApi.addItem(id, { qr_code: trimmed, comment: '' });
      setItems(prev => [...prev, r.data]);
      toast.success(`Added ${trimmed}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
  }, [id, isNew, items]);

  const addManual = () => {
    const code = manualCode.trim();
    if (!code) return;
    setManualCode('');
    onScan(code);
  };

  const updateItemComment = async (item, newComment) => {
    // Optimistic local update
    setItems(prev => prev.map(p => p.id === item.id ? { ...p, comment: newComment } : p));
    if (isNew) return; // Will be persisted on save
    try {
      await quotationRequestApi.updateItem(id, item.id, { comment: newComment });
    } catch (e) { toast.error('Failed to save comment'); }
  };

  const deleteItem = async (item) => {
    setConfirmDeleteItemId(null);
    if (isNew || (item.id || '').startsWith('local-')) {
      setItems(prev => prev.filter(p => p.id !== item.id));
      return;
    }
    try {
      await quotationRequestApi.deleteItem(id, item.id);
      setItems(prev => prev.filter(p => p.id !== item.id));
      toast.success('Removed');
    } catch (e) { toast.error('Failed to remove'); }
  };

  const saveAll = async () => {
    if (!header.customer_name.trim()) { toast.error('Customer name is required'); return; }
    setSaving(true);
    try {
      if (isNew) {
        const r = await quotationRequestApi.create({
          ...header,
          items: items.map(it => ({ qr_code: it.qr_code, comment: it.comment || '' })),
        });
        toast.success('Created');
        navigate(`/quotation-requests/${r.data.id}`, { replace: true });
      } else {
        await quotationRequestApi.update(id, header);
        toast.success('Saved');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const exportExcel = async () => {
    if (isNew) { toast.error('Save the request first to export.'); return; }
    try {
      const r = await quotationRequestApi.exportSingle(id);
      const safe = (header.customer_name || 'request').replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_');
      _download(r.data, `quotation_request_${safe}.xlsx`);
      toast.success('Exported');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to export'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-neutral-950">
        <Header title="Quotation Request" />
        <div className="px-3 md:px-8 py-3 md:py-5 max-w-screen-md mx-auto space-y-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      </div>
    );
  }

  const headerSummary = [header.gold_karat, header.diamond_quality, header.payment_terms].filter(Boolean).join(' · ');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="quotation-request-detail-page">
      <Header title={isNew ? 'New Quotation Request' : (header.customer_name || 'Quotation Request')} />
      <div className="px-3 md:px-8 py-3 md:py-5 max-w-screen-md mx-auto space-y-3">
        {/* Top action bar */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/quotation-requests')} className="-ml-2" data-testid="qr-detail-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="flex-1" />
          {!isNew && (
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="qr-detail-export">
              <FileSpreadsheet className="h-4 w-4 md:mr-1" /> <span className="hidden sm:inline">Export</span>
            </Button>
          )}
          <Button
            size="sm"
            onClick={saveAll}
            disabled={saving || !header.customer_name.trim() || !canEdit}
            className="bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white"
            data-testid="qr-detail-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin md:mr-1" /> : <Save className="h-4 w-4 md:mr-1" />}
            <span className="hidden sm:inline">{isNew ? 'Create' : 'Save'}</span>
          </Button>
        </div>

        {/* Collapsible header card */}
        <Card data-testid="qr-detail-header-card">
          <CardContent className="p-3 md:p-4">
            <button
              type="button"
              className="w-full flex items-center justify-between gap-2 text-left"
              onClick={() => setHeaderCollapsed(c => !c)}
              data-testid="qr-detail-header-toggle"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Customer & Terms</p>
                <p className="font-semibold text-sm md:text-base truncate">
                  {header.customer_name || <span className="text-muted-foreground italic font-normal">Enter customer name…</span>}
                </p>
                {headerCollapsed && headerSummary && (
                  <p className="text-[11px] text-muted-foreground truncate">{headerSummary}</p>
                )}
              </div>
              {headerCollapsed
                ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                : <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            </button>

            {!headerCollapsed && (
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs">Customer name *</Label>
                  <Input
                    value={header.customer_name}
                    onChange={e => setHeader({ ...header, customer_name: e.target.value })}
                    placeholder="e.g. Tanishq — Bangalore"
                    autoFocus={isNew}
                    data-testid="qr-detail-customer"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <Label className="text-xs">Gold karat</Label>
                    <Input
                      value={header.gold_karat}
                      onChange={e => setHeader({ ...header, gold_karat: e.target.value })}
                      placeholder="e.g. 18K / 22K"
                      data-testid="qr-detail-karat"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diamond quality</Label>
                    <Input
                      value={header.diamond_quality}
                      onChange={e => setHeader({ ...header, diamond_quality: e.target.value })}
                      placeholder="e.g. VVS / SI / G-H"
                      data-testid="qr-detail-dia-quality"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Diamond pricing</Label>
                    <Input
                      value={header.diamond_pricing}
                      onChange={e => setHeader({ ...header, diamond_pricing: e.target.value })}
                      placeholder="e.g. ₹3500/ct"
                      data-testid="qr-detail-dia-pricing"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Payment terms</Label>
                    <Input
                      value={header.payment_terms}
                      onChange={e => setHeader({ ...header, payment_terms: e.target.value })}
                      placeholder="e.g. 50% advance, net 30"
                      data-testid="qr-detail-terms"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Remarks</Label>
                  <Textarea
                    value={header.remarks}
                    onChange={e => setHeader({ ...header, remarks: e.target.value })}
                    rows={2}
                    placeholder="Anything else to record?"
                    data-testid="qr-detail-remarks"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scanner toggle / manual entry */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showScanner ? 'outline' : 'default'}
            onClick={() => setShowScanner(s => !s)}
            className={cn(!showScanner && 'bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white')}
            data-testid="qr-detail-toggle-scanner"
          >
            {showScanner ? <X className="h-4 w-4 mr-1" /> : <Camera className="h-4 w-4 mr-1" />}
            {showScanner ? 'Stop camera' : 'Scan QR'}
          </Button>
          <div className="relative flex-1">
            <QrCode className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={manualCode}
              onChange={e => setManualCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }}
              placeholder="Or type a code & press Enter"
              className="pl-9"
              data-testid="qr-detail-manual-input"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addManual}
            disabled={!manualCode.trim()}
            data-testid="qr-detail-manual-add"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* Camera */}
        {showScanner && <QRScanner onScan={onScan} onClose={() => setShowScanner(false)} />}

        {/* Items list */}
        <div className="space-y-2" data-testid="qr-detail-items-list">
          <div className="flex items-center justify-between px-1">
            <div className="text-xs text-muted-foreground flex items-center">
              <Badge variant="outline" className="text-[10px] mr-1.5">{items.length}</Badge>
              <span>design{items.length === 1 ? '' : 's'} scanned</span>
            </div>
            {isNew && items.length > 0 && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Tap Create to save</p>
            )}
          </div>
          {items.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <QrCode className="h-10 w-10 mx-auto text-fuchsia-400 mb-2" />
                <p className="text-sm text-muted-foreground">No designs scanned yet.</p>
                <p className="text-[11px] text-muted-foreground mt-1">Point your camera at a QR code or type the code above.</p>
              </CardContent>
            </Card>
          ) : (
            items.map((it, idx) => (
              <Card key={it.id} className="overflow-hidden" data-testid={`qr-detail-item-${it.qr_code}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-md bg-fuchsia-50 dark:bg-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-fuchsia-700 dark:text-fuchsia-300 tabular-nums">{idx + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-mono text-xs md:text-sm font-semibold truncate">{it.qr_code}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-rose-500 flex-shrink-0"
                          onClick={() => setConfirmDeleteItemId(it.id)}
                          data-testid={`qr-detail-item-delete-${it.qr_code}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <Textarea
                        value={it.comment || ''}
                        onChange={e => updateItemComment(it, e.target.value)}
                        placeholder="Comment on this design (optional)"
                        rows={1}
                        className="text-xs resize-none"
                        data-testid={`qr-detail-item-comment-${it.qr_code}`}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmDeleteItemId} onOpenChange={(o) => !o && setConfirmDeleteItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this design?</AlertDialogTitle>
            <AlertDialogDescription>
              The scanned QR code and its comment will be removed from this quotation request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteItem(items.find(i => i.id === confirmDeleteItemId))}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="qr-detail-item-confirm-delete"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

function _download(blob, filename) {
  const url = URL.createObjectURL(new Blob([blob]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default QuotationRequestDetail;
